import { Box, Stack, Typography } from '@mui/material'

import { BLUE_100, BLUE_600, GRAY_400, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { ModeSelectorProps } from 'models/visualStaging/virtualStagingTypes'
import { ProcessingMode } from 'constants/visualStaging/visualStagingTypes'
import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * ModeSelector component for Virtual Staging.
 * Matches Figma design with radio button styling.
 * Allows users to select between "Apply to entire image", "Preserve specific zone", and "Replace specific zone".
 * 
 * @example
 * <ModeSelector
 *   processingMode={virtualStagingState.processingMode}
 *   onProcessingModeChange={changeProcessingMode}
 *   isDisabled={virtualStagingState.isGeneratingStaging}
 * />
 */
export const ModeSelector: React.FC<ModeSelectorProps> = ({
  processingMode,
  onProcessingModeChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])

  const processingOptions = [
    {
      value: ProcessingMode.ENTIRE_IMAGE,
      displayName: t('virtual_staging.processing_mode.entire_image'),
    },
    {
      value: ProcessingMode.PRESERVE_ZONE,
      displayName: t('virtual_staging.processing_mode.preserve_zone'),
    },
    {
      value: ProcessingMode.REPLACE_ZONE,
      displayName: t('virtual_staging.processing_mode.replace_zone'),
    },
  ]

  const isSelected = (mode: ProcessingMode) => processingMode === mode

  const handleSelect = (mode: ProcessingMode) => {
    if (!isDisabled) {
      onProcessingModeChange(mode)
    }
  }

  return (
    <Stack gap={0.6}>
      <Typography
        sx={{
          fontSize: '1.4rem',
          fontWeight: 400,
          color: GRAY_700,
          lineHeight: 1.4,
          letterSpacing: '-0.56px',
        }}
      >
        {t('virtual_staging.processing_mode.title')}
      </Typography>
      <Stack gap={0.8}>
        {processingOptions.map((option) => {
          const selected = isSelected(option.value)
          return (
            <Box
              key={option.value}
              onClick={() => handleSelect(option.value)}
              sx={{
                display: 'flex',
                gap: '0.8rem',
                alignItems: 'flex-start',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.6 : 1,
              }}
            >
              {/* Radio button */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingTop: '0.4rem',
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: '1.6rem',
                    height: '1.6rem',
                    borderRadius: '100px',
                    border: `1px solid ${selected ? BLUE_600 : GRAY_400}`,
                    backgroundColor: selected ? BLUE_100 : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {selected && (
                    <Box
                      sx={{
                        width: '0.8rem',
                        height: '0.8rem',
                        borderRadius: '100px',
                        backgroundColor: BLUE_600,
                      }}
                    />
                  )}
                </Box>
              </Box>
              {/* Label */}
              <Typography
                sx={{
                  flex: 1,
                  fontSize: '1.6rem',
                  fontStyle: 'normal',
                  fontWeight: 450,
                  lineHeight: 1.5, // 150% = 24px
                  letterSpacing: '-0.64px',
                  color: selected ? GRAY_900 : GRAY_700,
                }}
              >
                {option.displayName}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}
